import { useCallback, useEffect, useState } from 'react';
import { TabData } from '@/__core/types/TabData';

export interface UseTabStorageOptions {
  storageKey: string;
  defaultTabs: TabData[];
  maxTabs?: number;
  /** Tab types allowed to open multiple instances */
  allowMultipleTabTypes?: string[];
  /** Re-merge list/add tabs when defaultTabs changes (e.g. permissions) */
  syncDefaultTabsOnChange?: boolean;
}

const DEFAULT_ALLOW_MULTIPLE_TYPES = [
  'new-employee',
  'new-order',
  'new-customer',
  'new-user',
  'new-role',
  'new-group',
  'new-inventory-record',
  'sale-orders',
  'task-report',
];

const mergeDefaultTabs = (storedTabs: TabData[], defaultTabs: TabData[]): TabData[] => {
  const requiredDefaultTabs = defaultTabs.filter((tab) => tab.closable === false);
  const merged = [...storedTabs];

  requiredDefaultTabs.forEach((defaultTab) => {
    const exists = merged.some((tab) => tab.id === defaultTab.id || tab.type === defaultTab.type);
    if (!exists) {
      merged.push(defaultTab);
    }
  });

  return merged;
};

const readStoredActiveTabIndex = (storageKey: string, defaultTabs: TabData[]): number => {
  try {
    const storedActiveRaw = localStorage.getItem(`${storageKey}_active`);
    if (!storedActiveRaw) {
      return 0;
    }
    const parsed = parseInt(storedActiveRaw, 10);
    if (Number.isNaN(parsed) || parsed < 0) {
      return 0;
    }

    const storedTabsRaw = localStorage.getItem(storageKey);
    if (!storedTabsRaw) {
      return 0;
    }
    const parsedTabs = JSON.parse(storedTabsRaw);
    const mergedTabs = Array.isArray(parsedTabs) ? mergeDefaultTabs(parsedTabs, defaultTabs) : defaultTabs;

    if (parsed >= mergedTabs.length) {
      return 0;
    }
    return parsed;
  } catch {
    return 0;
  }
};

export const useTabStorage = ({
  storageKey,
  defaultTabs,
  maxTabs = 20,
  allowMultipleTabTypes = DEFAULT_ALLOW_MULTIPLE_TYPES,
  syncDefaultTabsOnChange = true,
}: UseTabStorageOptions) => {
  const [tabs, setTabs] = useState<TabData[]>(() => {
    try {
      const savedTabs = localStorage.getItem(storageKey);
      if (savedTabs) {
        const parsedTabs = JSON.parse(savedTabs);
        if (Array.isArray(parsedTabs)) {
          return mergeDefaultTabs(parsedTabs, defaultTabs);
        }
      }
    } catch (error) {
      console.warn('Failed to load tabs from localStorage:', error);
    }
    return defaultTabs;
  });

  const [activeTab, setActiveTab] = useState(() => readStoredActiveTabIndex(storageKey, defaultTabs));

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(tabs));
    } catch (error) {
      console.warn('Failed to save tabs to localStorage:', error);
    }
  }, [tabs, storageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(`${storageKey}_active`, activeTab.toString());
    } catch (error) {
      console.warn('Failed to save active tab to localStorage:', error);
    }
  }, [activeTab, storageKey]);

  useEffect(() => {
    if (!syncDefaultTabsOnChange) return;

    setTabs((prevTabs) => {
      const userTabs = prevTabs.filter((tab) => tab.type !== 'list' && tab.type !== 'add');
      const addTab = defaultTabs.find((tab) => tab.type === 'add');
      const listTab = defaultTabs.find((tab) => tab.type === 'list');
      const newTabs: TabData[] = [];

      if (listTab) {
        newTabs.push(listTab);
      }
      newTabs.push(...userTabs);
      if (addTab) {
        newTabs.push(addTab);
      }

      return newTabs.slice(0, maxTabs);
    });
  }, [defaultTabs, maxTabs, syncDefaultTabsOnChange]);

  const addTab = useCallback(
    (newTab: Omit<TabData, 'createdAt'> | TabData) => {
      setTabs((prevTabs) => {
        const hasEntityId = Boolean(newTab.entityId);
        const isUpdateTab = typeof newTab.type === 'string' && newTab.type.startsWith('update-');

        const existingTabIndex = prevTabs.findIndex((tab) => {
          if (tab.type !== newTab.type) return false;

          if (isUpdateTab && newTab.entityId) {
            return tab.entityId === newTab.entityId;
          }

          if (hasEntityId) {
            return tab.entityId === newTab.entityId;
          }

          if (allowMultipleTabTypes.includes(newTab.type)) {
            return false;
          }

          return true;
        });

        if (existingTabIndex !== -1) {
          return prevTabs;
        }

        const closableTabs = prevTabs.filter((tab) => tab.closable !== false);
        if (closableTabs.length >= maxTabs) {
          const oldestTab = closableTabs
            .filter((tab) => tab.type !== 'list' && tab.type !== 'add')
            .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))[0];

          if (oldestTab) {
            return prevTabs.filter((tab) => tab.id !== oldestTab.id);
          }
        }

        const tabWithTimestamp: TabData = {
          ...newTab,
          createdAt:
            'createdAt' in newTab && typeof newTab.createdAt === 'number'
              ? newTab.createdAt
              : Date.now(),
        };

        const addTabItem = prevTabs.find((tab) => tab.type === 'add');
        const otherTabs = prevTabs.filter((tab) => tab.type !== 'add');

        if (addTabItem) {
          return [...otherTabs, tabWithTimestamp, addTabItem];
        }

        return [...prevTabs, tabWithTimestamp];
      });
    },
    [allowMultipleTabTypes, maxTabs]
  );

  const removeTab = useCallback(
    (tabId: number) => {
      setTabs((prevTabs) => {
        const tabIndex = prevTabs.findIndex((tab) => tab.id === tabId);
        if (tabIndex === -1) return prevTabs;

        const newTabs = prevTabs.filter((tab) => tab.id !== tabId);

        let newActiveTab = activeTab;
        if (activeTab === tabIndex) {
          newActiveTab = 0;
        } else if (activeTab > tabIndex) {
          newActiveTab = activeTab - 1;
        }

        if (newActiveTab !== activeTab) {
          setTimeout(() => setActiveTab(newActiveTab), 0);
        }

        return newTabs;
      });
    },
    [activeTab]
  );

  const updateTab = useCallback((tabId: number, updates: Partial<TabData>) => {
    setTabs((prevTabs) => prevTabs.map((tab) => (tab.id === tabId ? { ...tab, ...updates } : tab)));
  }, []);

  const clearTabs = useCallback(() => {
    setTabs(defaultTabs);
    setActiveTab(0);
  }, [defaultTabs]);

  return {
    tabs,
    activeTab,
    setActiveTab,
    addTab,
    removeTab,
    closeTab: removeTab,
    updateTab,
    clearTabs,
    resetTabs: clearTabs,
  };
};
